import { useFlags } from 'launchdarkly-react-client-sdk';

import { useDerivedNetworkContext } from '@/context';
import { useReleaseFlag } from '@/hooks';
import { useFileFetcher } from '@/services/queryUtils';
import type { ChartGranularity, DateRange, ShowFilter } from '@/types';
import { downloadFile } from '@/utils/dataDownloadUtils';
import { getDataAvailability } from './useIsBeforeDataAvailability';

interface TopPlayersExportResult {
  warningMessage?: string;
}

/**
 * Calls the server-side top apps and websites export endpoint and triggers a file download.
 */
const useTopPlayersCsvExport = () => {
  const fetchFile = useFileFetcher();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { audienceSources, showAudienceSocialViews } = useFlags<{
    audienceSources?: string[];
    showAudienceSocialViews?: boolean;
  }>();
  const sources = audienceSources ?? ['acast', 'phyllo'];
  const shouldExcludeSocials = showAudienceSocialViews !== true;
  const flag = useReleaseFlag('audience_page_backfill');

  const handleExport = async (
    dateRange: DateRange | undefined,
    selectedShow: ShowFilter,
    granularity: ChartGranularity
  ): Promise<TopPlayersExportResult | void> => {
    if (!dateRange) return undefined;

    const { hideContent, queryFrom } = getDataAvailability(
      'players',
      dateRange.from,
      dateRange.to
    );
    if (hideContent) return undefined;

    const queryParams = new URLSearchParams({
      from: queryFrom,
      to: dateRange.to,
      granularity,
    });
    if (selectedShow && selectedShow !== 'all') {
      queryParams.append('showIds', selectedShow);
    }
    queryParams.append('excludeSocials', String(shouldExcludeSocials));
    sources.forEach((source) => queryParams.append('sources', source));
    if (flag) {
      queryParams.append('flag', flag);
    }

    const path = `/v2/networks/${selectedNetwork.id}/audience/top-players/export?${queryParams.toString()}`;
    const { blob, fileName, warningMessage } = await fetchFile(path);

    downloadFile(blob, fileName);
    return { warningMessage };
  };

  const handleTopPlayersCSVExport = (
    dateRange: DateRange | undefined,
    selectedShow: ShowFilter,
    granularity: ChartGranularity
  ): Promise<TopPlayersExportResult | void> => handleExport(dateRange, selectedShow, granularity);

  return { handleTopPlayersCSVExport };
};

export default useTopPlayersCsvExport;
